import React, { useState } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FactoryRegistrationProfileManagement = () => {
  const [registrationStep, setRegistrationStep] = useState(1);
  const [registrationData, setRegistrationData] = useState({
    // Basic Information
    factoryName: '',
    registrationNumber: '',
    establishedYear: '',
    factoryType: '',
    primaryContact: '',
    secondaryContact: '',
    email: '',
    website: '',
    
    // Location Services
    address: '',
    city: '',
    state: '',
    pincode: '',
    gpsLat: '',
    gpsLng: '',
    deliveryRadius: '',
    
    // Production Capabilities
    sugarGrades: [],
    annualCapacity: '',
    dailyProduction: '',
    seasonalPattern: 'year-round',
    qualityParams: '',
    
    // Documents
    gstNumber: '',
    gstCertificate: null,
    fssaiLicense: '',
    fssaiCertificate: null,
    factoryLicense: null,
    bankDetails: '',
    
    // Images
    factoryImages: []
  });

  const [existingFactories, setExistingFactories] = useState([
    {
      id: 1,
      factoryName: 'Maharashtra Sugar Mills',
      registrationNumber: 'MH-SGR-2020-001',
      location: 'Pune, Maharashtra',
      status: 'verified',
      verificationDate: '2025-11-01',
      gstVerified: true,
      fssaiVerified: true,
      annualCapacity: 50000,
      sugarGrades: ['ICUMSA 45', 'VHP', 'Raw Sugar']
    },
    {
      id: 2,
      factoryName: 'Gujarat Sugar Industries',
      registrationNumber: 'GJ-SGR-2021-002',
      location: 'Ahmedabad, Gujarat',
      status: 'pending',
      verificationDate: null,
      gstVerified: true,
      fssaiVerified: false,
      annualCapacity: 35000,
      sugarGrades: ['ICUMSA 45', 'Refined Sugar']
    }
  ]);

  const sugarGradeOptions = ['ICUMSA 45', 'VHP', 'Raw Sugar', 'Refined Sugar', 'Plantation White', 'Demerara', 'Muscovado'];
  const factoryTypes = ['Sugar Mill', 'Refinery', 'Co-operative Mill', 'Private Mill'];
  const seasonalPatterns = ['Year-round', 'Seasonal (Oct-Apr)', 'Seasonal (Nov-May)', 'Custom Schedule'];

  const totalSteps = 4;

  const handleInputChange = (field, value) => {
    setRegistrationData({ ...registrationData, [field]: value });
  };

  const handleGradeToggle = (grade) => {
    const currentGrades = registrationData?.sugarGrades;
    if (currentGrades?.includes(grade)) {
      setRegistrationData({ 
        ...registrationData, 
        sugarGrades: currentGrades?.filter(g => g !== grade) 
      });
    } else {
      setRegistrationData({ 
        ...registrationData, 
        sugarGrades: [...currentGrades, grade] 
      });
    }
  };

  const handleFileUpload = (field, file) => {
    setRegistrationData({ ...registrationData, [field]: file });
  };

  const handleNext = () => {
    if (registrationStep < totalSteps) {
      setRegistrationStep(registrationStep + 1);
    }
  };

  const handlePrevious = () => {
    if (registrationStep > 1) {
      setRegistrationStep(registrationStep - 1);
    }
  };

  const handleSubmit = () => {
    // Validate all required fields
    if (!registrationData?.factoryName || !registrationData?.registrationNumber || !registrationData?.gstNumber) {
      alert('Please fill all required fields');
      return;
    }

    // Create new factory entry
    const newFactory = {
      id: existingFactories?.length + 1,
      factoryName: registrationData?.factoryName,
      registrationNumber: registrationData?.registrationNumber,
      location: `${registrationData?.city}, ${registrationData?.state}`,
      status: 'pending',
      verificationDate: null,
      gstVerified: false,
      fssaiVerified: false,
      annualCapacity: parseInt(registrationData?.annualCapacity) || 0,
      sugarGrades: registrationData?.sugarGrades
    };

    setExistingFactories([...existingFactories, newFactory]);
    alert('Factory registration submitted successfully! Verification pending.');
    
    // Reset form
    setRegistrationStep(1);
    setRegistrationData({
      factoryName: '',
      registrationNumber: '',
      establishedYear: '',
      factoryType: '',
      primaryContact: '',
      secondaryContact: '',
      email: '',
      website: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      gpsLat: '',
      gpsLng: '',
      deliveryRadius: '',
      sugarGrades: [],
      annualCapacity: '',
      dailyProduction: '',
      seasonalPattern: 'year-round',
      qualityParams: '',
      gstNumber: '',
      gstCertificate: null,
      fssaiLicense: '',
      fssaiCertificate: null,
      factoryLicense: null,
      bankDetails: '',
      factoryImages: []
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      verified: { label: 'Verified', color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      pending: { label: 'Pending Verification', color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: 'XCircle' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config?.color}`}>
        <Icon name={config?.icon} size={14} />
        {config?.label}
      </span>
    );
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {[1, 2, 3, 4]?.map((step) => (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                registrationStep >= step
                  ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {step}
              </div>
              <span className="text-xs mt-2 text-center text-muted-foreground">
                {step === 1 && 'Basic Info'}
                {step === 2 && 'Location'}
                {step === 3 && 'Production'}
                {step === 4 && 'Documents'}
              </span>
            </div>
            {step < 4 && (
              <div className={`flex-1 h-1 mx-2 ${
                registrationStep > step ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00]' : 'bg-muted'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderBasicInformation = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-foreground mb-4">Basic Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Factory Name *</label>
          <input
            type="text"
            value={registrationData?.factoryName}
            onChange={(e) => handleInputChange('factoryName', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter factory name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Registration Number *</label>
          <input
            type="text"
            value={registrationData?.registrationNumber}
            onChange={(e) => handleInputChange('registrationNumber', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="XX-SGR-YYYY-NNN"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Established Year</label>
          <input
            type="number"
            value={registrationData?.establishedYear}
            onChange={(e) => handleInputChange('establishedYear', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="YYYY"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Factory Type *</label>
          <select
            value={registrationData?.factoryType}
            onChange={(e) => handleInputChange('factoryType', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
          >
            <option value="">Select type</option>
            {factoryTypes?.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Primary Contact *</label>
          <input
            type="tel"
            value={registrationData?.primaryContact}
            onChange={(e) => handleInputChange('primaryContact', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="+91 XXXXX XXXXX"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Secondary Contact</label>
          <input
            type="tel"
            value={registrationData?.secondaryContact}
            onChange={(e) => handleInputChange('secondaryContact', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="+91 XXXXX XXXXX"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
          <input
            type="email"
            value={registrationData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="contact@factory.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Website</label>
          <input
            type="url"
            value={registrationData?.website}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="https://www.factory.com"
          />
        </div>
      </div>
    </div>
  );

  const renderLocationServices = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-foreground mb-4">Location Services</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-2">Complete Address *</label>
          <textarea
            value={registrationData?.address}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter complete factory address"
            rows="3"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">City *</label>
          <input
            type="text"
            value={registrationData?.city}
            onChange={(e) => handleInputChange('city', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="City"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">State *</label>
          <input
            type="text"
            value={registrationData?.state}
            onChange={(e) => handleInputChange('state', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="State"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Pincode *</label>
          <input
            type="text"
            value={registrationData?.pincode}
            onChange={(e) => handleInputChange('pincode', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="000000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Delivery Radius (km)</label>
          <input
            type="number"
            value={registrationData?.deliveryRadius}
            onChange={(e) => handleInputChange('deliveryRadius', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter delivery zone radius"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">GPS Latitude</label>
          <input
            type="text"
            value={registrationData?.gpsLat}
            onChange={(e) => handleInputChange('gpsLat', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="00.000000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">GPS Longitude</label>
          <input
            type="text"
            value={registrationData?.gpsLng}
            onChange={(e) => handleInputChange('gpsLng', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="00.000000"
          />
        </div>
      </div>
    </div>
  );

  const renderProductionCapabilities = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-foreground mb-4">Production Capabilities</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-2">Sugar Grades Produced *</label>
          <div className="flex flex-wrap gap-2">
            {sugarGradeOptions?.map(grade => (
              <label key={grade} className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-border rounded-lg hover:bg-muted">
                <input
                  type="checkbox"
                  checked={registrationData?.sugarGrades?.includes(grade)}
                  onChange={() => handleGradeToggle(grade)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">{grade}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Annual Capacity (tons) *</label>
          <input
            type="number"
            value={registrationData?.annualCapacity}
            onChange={(e) => handleInputChange('annualCapacity', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter annual production capacity"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Daily Production (tons)</label>
          <input
            type="number"
            value={registrationData?.dailyProduction}
            onChange={(e) => handleInputChange('dailyProduction', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter daily production"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Seasonal Pattern *</label>
          <select
            value={registrationData?.seasonalPattern}
            onChange={(e) => handleInputChange('seasonalPattern', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
          >
            {seasonalPatterns?.map(pattern => (
              <option key={pattern} value={pattern}>{pattern}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-2">Quality Parameters</label>
          <textarea
            value={registrationData?.qualityParams}
            onChange={(e) => handleInputChange('qualityParams', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter quality certifications, standards, and parameters"
            rows="3"
          />
        </div>
      </div>
    </div>
  );

  const renderDocumentManagement = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-foreground mb-4">Document Management</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">GST Number *</label>
          <input
            type="text"
            value={registrationData?.gstNumber}
            onChange={(e) => handleInputChange('gstNumber', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter GST number"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">GST Certificate Upload *</label>
          <input
            type="file"
            onChange={(e) => handleFileUpload('gstCertificate', e?.target?.files?.[0])}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            accept=".pdf,.jpg,.jpeg,.png"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">FSSAI License Number *</label>
          <input
            type="text"
            value={registrationData?.fssaiLicense}
            onChange={(e) => handleInputChange('fssaiLicense', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Enter FSSAI license"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">FSSAI Certificate Upload *</label>
          <input
            type="file"
            onChange={(e) => handleFileUpload('fssaiCertificate', e?.target?.files?.[0])}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            accept=".pdf,.jpg,.jpeg,.png"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Factory License Upload</label>
          <input
            type="file"
            onChange={(e) => handleFileUpload('factoryLicense', e?.target?.files?.[0])}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            accept=".pdf,.jpg,.jpeg,.png"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Bank Details</label>
          <input
            type="text"
            value={registrationData?.bankDetails}
            onChange={(e) => handleInputChange('bankDetails', e?.target?.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            placeholder="Bank name, account number, IFSC"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-2">Factory Images (Multiple)</label>
          <input
            type="file"
            onChange={(e) => handleFileUpload('factoryImages', Array.from(e?.target?.files || []))}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            accept="image/*"
            multiple
          />
          <p className="text-xs text-muted-foreground mt-1">Upload exterior, production floor, quality control, and storage facility images</p>
        </div>
      </div>
    </div>
  );

  const renderRegistrationWizard = () => (
    <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
      {renderStepIndicator()}
      
      <div className="mb-6">
        {registrationStep === 1 && renderBasicInformation()}
        {registrationStep === 2 && renderLocationServices()}
        {registrationStep === 3 && renderProductionCapabilities()}
        {registrationStep === 4 && renderDocumentManagement()}
      </div>

      <div className="flex justify-between gap-3">
        <Button
          onClick={handlePrevious}
          disabled={registrationStep === 1}
          variant="outline"
        >
          <Icon name="ChevronLeft" size={20} className="mr-2" />
          Previous
        </Button>
        
        {registrationStep < totalSteps ? (
          <Button
            onClick={handleNext}
            className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
          >
            Next
            <Icon name="ChevronRight" size={20} className="ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white"
          >
            <Icon name="CheckCircle" size={20} className="mr-2" />
            Submit Registration
          </Button>
        )}
      </div>
    </div>
  );

  const renderExistingProfiles = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground">Registered Factory Profiles</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {existingFactories?.map(factory => (
          <div key={factory?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-foreground">{factory?.factoryName}</h3>
                <p className="text-sm text-muted-foreground">{factory?.registrationNumber}</p>
              </div>
              {getStatusBadge(factory?.status)}
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{factory?.location}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Factory" size={16} className="text-muted-foreground" />
                <span className="text-foreground">Capacity: {factory?.annualCapacity?.toLocaleString()} tons/year</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {factory?.sugarGrades?.map(grade => (
                  <span key={grade} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                    {grade}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <div className="flex items-center gap-1 text-xs">
                {factory?.gstVerified ? (
                  <>
                    <Icon name="CheckCircle" size={14} className="text-green-600" />
                    <span className="text-green-600">GST Verified</span>
                  </>
                ) : (
                  <>
                    <Icon name="Clock" size={14} className="text-yellow-600" />
                    <span className="text-yellow-600">GST Pending</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs">
                {factory?.fssaiVerified ? (
                  <>
                    <Icon name="CheckCircle" size={14} className="text-green-600" />
                    <span className="text-green-600">FSSAI Verified</span>
                  </>
                ) : (
                  <>
                    <Icon name="Clock" size={14} className="text-yellow-600" />
                    <span className="text-yellow-600">FSSAI Pending</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Icon name="Edit" size={14} className="mr-2" />
                Edit Profile
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Icon name="Eye" size={14} className="mr-2" />
                View Details
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center">
                <Icon name="FileText" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Factory Registration & Profile Management</h1>
                <p className="text-muted-foreground">Complete factory onboarding with document verification and profile administration</p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {renderRegistrationWizard()}
            {renderExistingProfiles()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FactoryRegistrationProfileManagement;